import { useState } from "react";
import { Loader2, Plug, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { acpSettingsSave, acpSftpTest } from "@/lib/api";
import { Section, StatusPill } from "./acpUi";

// OpenAI merchant SFTP credentials (issued after approval at chatgpt.com/merchants). Secrets never come back to the browser.
export function AcpSftpSettings({ caller, sftp, onSaved }) {
  const [f, setF] = useState({ host: sftp.host, port: sftp.port, username: sftp.username, remote_dir: sftp.remote_dir, filename: sftp.filename, password: "", private_key: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  async function save() {
    setSaving(true);
    try {
      const r = await acpSettingsSave(caller, { sftp: f });
      onSaved(r);
      setF((x) => ({ ...x, password: "", private_key: "" }));
      toast.success(r.sftp.configured ? "SFTP delivery saved — you can push the feed now" : "SFTP details saved (add a password or private key to finish)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }
  async function test() {
    setTesting(true);
    try {
      const r = await acpSftpTest(caller);
      toast.success(`Connected to ${r.host} · ${r.files.length} file(s) in ${r.remote_dir}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed");
    } finally {
      setTesting(false);
    }
  }
  return (
    <Section title="OpenAI SFTP delivery (daily snapshot)" right={<StatusPill status={sftp.configured ? "connected" : "not_connected"} testId="acp-sftp-status" />}>
      <p className="text-xs text-zinc-500 mb-3">Paste the SFTP host, username and key from your ChatGPT merchant approval email. The full catalogue is uploaded as a gzipped JSONL snapshot every night at 07:15 UTC and whenever you press <b>Push to OpenAI now</b>.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2"><Label className="text-xs text-zinc-600">Host</Label><Input value={f.host} onChange={set("host")} placeholder="sftp.openai.com" className="h-10 rounded-xl mt-1" data-testid="acp-sftp-host" /></div>
        <div><Label className="text-xs text-zinc-600">Port</Label><Input value={f.port} onChange={set("port")} type="number" className="h-10 rounded-xl mt-1" data-testid="acp-sftp-port" /></div>
        <div><Label className="text-xs text-zinc-600">Username</Label><Input value={f.username} onChange={set("username")} className="h-10 rounded-xl mt-1" data-testid="acp-sftp-username" /></div>
        <div><Label className="text-xs text-zinc-600">Password {sftp.has_password && <span className="text-emerald-600">· saved</span>}</Label><Input value={f.password} onChange={set("password")} type="password" placeholder={sftp.has_password ? "•••••••• (leave blank to keep)" : "optional if using a key"} className="h-10 rounded-xl mt-1" data-testid="acp-sftp-password" /></div>
        <div><Label className="text-xs text-zinc-600">Remote folder</Label><Input value={f.remote_dir} onChange={set("remote_dir")} className="h-10 rounded-xl mt-1" data-testid="acp-sftp-remote-dir" /></div>
        <div className="sm:col-span-2"><Label className="text-xs text-zinc-600">Private key {sftp.has_private_key && <span className="text-emerald-600">· saved</span>}</Label><Textarea value={f.private_key} onChange={set("private_key")} rows={4} placeholder={sftp.has_private_key ? "Saved — paste a new key to replace" : "-----BEGIN OPENSSH PRIVATE KEY-----"} className="rounded-xl mt-1 font-mono text-[11px]" data-testid="acp-sftp-private-key" /></div>
        <div><Label className="text-xs text-zinc-600">File name (stable, overwritten)</Label><Input value={f.filename} onChange={set("filename")} className="h-10 rounded-xl mt-1" data-testid="acp-sftp-filename" /></div>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <button type="button" onClick={save} disabled={saving} className="h-9 px-3 rounded-lg bg-zinc-900 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="acp-sftp-save">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save SFTP details
        </button>
        <button type="button" onClick={test} disabled={testing || !sftp.configured} className="h-9 px-3 rounded-lg border border-zinc-200 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-zinc-50 disabled:opacity-50" data-testid="acp-sftp-test">
          {testing ? <Loader2 size={13} className="animate-spin" /> : <Plug size={13} />} Test connection
        </button>
      </div>
    </Section>
  );
}
